import * as Relations from "types/relation";
import * as Expressions from "types/expression";
import * as ExpressionUtils from "utils/expression";
import { ConditionUtils } from "types/condition";
import { filterUndefined, isArrEqual } from "utils/collect";
import { isInterval, isNotColumn } from "@/utils/insight/properties";
import { andAll, col } from "utils/expression";
export function sanitizeAliasForBQ(alias) {
    if (alias === undefined) {
        return alias;
    }
    return alias.replace(/\s+/, "_").replace(/[^\w_]+/, "");
}
export function isBQDataset(dataset) {
    return isBQDataSource(dataset?.source);
}
export function isBQDataSource(source) {
    if (source === undefined) {
        return false;
    }
    return (source.url.startsWith("gs://") ||
        (source.content.type == "CONNECTION_METADATA" && source.content.provider == "BIGQUERY"));
}
export function hasBQDataSource(relation) {
    return getSources(relation).some(isBQDataSource);
}
export function getAliasFormatter(relation) {
    if (hasBQDataSource(relation)) {
        return sanitizeAliasForBQ;
    }
    return (x) => x;
}
export function stripComposedTags(relation, targetDatasetId) {
    if (relation.type == "DATA_SOURCE" || relation.tag?.dataset_id == targetDatasetId) {
        return relation;
    }
    relation = { ...relation };
    delete relation.tag;
    if ("input" in relation) {
        return {
            ...relation,
            input: stripComposedTags(relation.input)
        };
    }
    switch (relation.type) {
        case "JOIN":
            return {
                ...relation,
                left: stripComposedTags(relation.left),
                right: stripComposedTags(relation.right)
            };
        case "UNION":
            return {
                ...relation,
                inputs: relation.inputs.map(stripComposedTags)
            };
        default:
            return relation;
    }
}
export function getLatestTag(relation) {
    if (relation.tag !== undefined) {
        return relation.tag;
    }
    if ("input" in relation) {
        return getLatestTag(relation.input);
    }
    switch (relation.type) {
        case "JOIN":
            return getLatestTag(relation.left);
        case "UNION":
            return relation.inputs.length == 0 ? undefined : getLatestTag(relation.inputs[0]);
        case "DATA_SOURCE":
            return relation.tag;
    }
}
export function getSources(relation) {
    if ("input" in relation) {
        return getSources(relation.input);
    }
    switch (relation.type) {
        case "JOIN":
            return getSources(relation.left).concat(getSources(relation.right));
        case "UNION":
            return relation.inputs.flatMap(getSources);
        case "DATA_SOURCE":
            return [relation.source];
    }
}
export function getSourceDialect(source) {
    if (source.content.type == "RAW_DATA") {
        return "duckdb";
    }
    switch (source.content.provider) {
        case "BIGQUERY":
            return "bigquery";
        case "DATABRICKS":
            return "databricks";
        case "ORACLE":
            return "oracle";
        case "POSTGRESQL":
            return "postgres";
        case "MSSQL":
            return "tsql";
        case "REDSHIFT":
            return "redshift";
        case "SNOWFLAKE":
            return "snowflake";
        case "TERADATA":
            return "teradata";
        default:
            return "duckdb";
    }
}
export function getParentDatasetIds(relation) {
    const parents = relation.tag?.dataset_id === undefined ? [] : [relation.tag?.dataset_id];
    if ("input" in relation) {
        return getParentDatasetIds(relation.input).concat(parents);
    }
    switch (relation.type) {
        case "JOIN":
            return getParentDatasetIds(relation.left).concat(getParentDatasetIds(relation.right)).concat(parents);
        case "UNION":
            return relation.inputs.flatMap(getParentDatasetIds).concat(parents);
        case "DATA_SOURCE":
            return parents;
    }
}
export function isCommonJoinCondition(condition) {
    return "key" in condition;
}
export function getLeftKey(condition) {
    return isCommonJoinCondition(condition) ? condition.key : condition.left_key;
}
export function getRightKey(condition) {
    return isCommonJoinCondition(condition) ? condition.key : condition.right_key;
}
export function shallowValidate(relation) {
    return validate(relation, true);
}
export function validate(relation, shallow = false) {
    if ("input" in relation) {
        const context = getOutputContext(relation.input);
        const errors = shallow ? [] : validate(relation.input);
        if (relation.type == "AGGREGATE") {
            const projections = relation.groupings.concat(relation.measures);
            return errors.concat(ExpressionUtils.validateProjections(context, projections));
        }
        else if (relation.type == "FILTER") {
            return errors.concat(ExpressionUtils.validate(context, relation.filter));
        }
        else if (relation.type == "PROJECT") {
            if (relation.project.length === 0) {
                errors.push(Expressions.customValidationError("At least one column must be included"));
            }
            return errors.concat(ExpressionUtils.validateProjections(context, relation.project));
        }
        else if (relation.type == "MUTATE") {
            return errors.concat(ExpressionUtils.validateProjections(context, relation.mutate));
        }
        else if (relation.type == "EXCLUDE") {
            return errors;
        }
        else if (relation.type == "DISTINCT") {
            if (relation.subset) {
                return errors.concat(ExpressionUtils.validateProjections(context, relation.subset));
            }
            return errors;
        }
        else if (relation.type == "SORT") {
            const sorts = relation.sort_by.map((x) => x.column);
            return ExpressionUtils.validateProjections(context, sorts);
        }
        else if (relation.type == "SQL") {
            if (relation.output_schema === undefined) {
                return errors.concat([Expressions.customValidationError("Custom SQL has not been validated.")]);
            }
            return errors;
        }
        else {
            return validate(compile(relation));
        }
    }
    if (relation.type == "DATA_SOURCE") {
        return [];
    }
    else if (relation.type == "JOIN") {
        const leftContext = getOutputContext(relation.left);
        const rightContext = getOutputContext(relation.right);
        let errors = [];
        if (relation.conditions.length === 0) {
            errors.push(Expressions.customValidationError("At least one join condition must be specified"));
        }
        if (!shallow) {
            errors = errors.concat(validate(relation.left));
            errors = errors.concat(validate(relation.right));
        }
        for (const condition of relation.conditions) {
            if ("key" in condition) {
                errors = errors
                    .concat(ExpressionUtils.validateProjection(leftContext, condition.key))
                    .concat(ExpressionUtils.validateProjection(rightContext, condition.key));
            }
            else {
                errors = errors
                    .concat(ExpressionUtils.validateProjection(leftContext, condition.left_key))
                    .concat(ExpressionUtils.validateProjection(rightContext, condition.right_key));
            }
        }
        return errors;
    }
    else if (relation.type == "UNION") {
        if (relation.inputs.length == 0) {
            return [Expressions.customValidationError("No rows selected")];
        }
        const errors = shallow ? [] : relation.inputs.flatMap((r) => validate(r));
        const first = getOutputContext(relation.inputs[0]);
        for (const input of relation.inputs.slice(1)) {
            const context = getOutputContext(input);
            ExpressionUtils.getSchemaColumns(context)
                .flatMap((column) => ExpressionUtils.validateColumn(first, column, [context[column].type]))
                .forEach((error) => errors.push(error));
        }
        return errors;
    }
    throw Error(`unknown relation: ${JSON.stringify(relation)}`);
}
export function contextFromSchemaItems(schemaItems) {
    return (schemaItems ?? []).reduce((context, field) => {
        const fieldType = field.type == "VARCHAR" ? "TEXT" : field.type;
        context[field.name] = {
            type: fieldType,
            values: field.values
        };
        return context;
    }, {});
}
export function getOutputContext(relation) {
    if (relation.tag?.schema !== undefined) {
        return contextFromSchemaItems(relation.tag.schema);
    }
    if ("input" in relation) {
        const context = getOutputContext(relation.input);
        if (relation.type == "DISTINCT" || relation.type == "FILTER" || relation.type == "SORT") {
            return context;
        }
        else if (relation.type == "PROJECT") {
            return ExpressionUtils.getOutputContext(context, relation.project);
        }
        else if (relation.type == "AGGREGATE") {
            return ExpressionUtils.getOutputContext(context, relation.groupings.concat(relation.measures));
        }
        else if (relation.type == "SQL") {
            return relation.output_schema === undefined ? context : contextFromSchemaItems(relation.output_schema);
        }
        else {
            return getOutputContext(compile(relation));
        }
    }
    else if (relation.type == "DATA_SOURCE") {
        return contextFromSchemaItems(relation.source.content.schema);
    }
    else if (relation.type == "JOIN") {
        const leftContext = getOutputContext(relation.left);
        const rightContext = getOutputContext(relation.right);
        const rightKeys = relation.conditions.map((c) => ("key" in c ? c.key : c.right_key));
        return ExpressionUtils.getSchemaColumns(rightContext).reduce((context, column) => {
            const rightColContext = rightContext[column];
            if (rightKeys.indexOf(column) == -1) {
                if (relation.suffix && leftContext[column] !== undefined) {
                    column = column + relation.suffix;
                }
            }
            context[column] = rightColContext;
            return context;
        }, leftContext);
    }
    else if (relation.type == "UNION") {
        if (relation.inputs.length == 0) {
            return {};
        }
        return getOutputContext(relation.inputs[0]);
    }
    throw Error(`unknown relation: ${JSON.stringify(relation)}`);
}
export function compile(relation) {
    if (relation.type == "MUTATE") {
        const context = getOutputContext(relation.input);
        let mutateContext = ExpressionUtils.getOutputContext(context, relation.mutate);
        let additions = relation.mutate;
        const updated = [];
        for (const column of ExpressionUtils.getSchemaColumns(context)) {
            if (mutateContext[column] !== undefined) {
                const match = additions.find((x) => ExpressionUtils.getAlias(x) == column);
                if (match !== undefined) {
                    updated.push(match);
                    additions = additions.filter((x) => ExpressionUtils.getAlias(x) != column);
                    mutateContext = ExpressionUtils.getOutputContext(context, additions);
                }
            }
            else {
                updated.push(column);
            }
        }
        return Relations.project(relation.input, updated.concat(additions));
    }
    else if (relation.type == "EXCLUDE") {
        const projections = ExpressionUtils.getSchemaColumns(getOutputContext(relation.input)).filter((column) => relation.exclude_columns.indexOf(column) == -1);
        return Relations.project(relation.input, projections);
    }
    else if (relation.type == "PIVOT") {
        const valueColumn = Expressions.projectionToExpression(relation.value_column);
        const measures = relation.wide_values.map((value) => {
            const col = ExpressionUtils.col(relation.wide_column);
            const filter = value === undefined || value === null ? col.isNull() : col.eq(value);
            return ExpressionUtils.builderFrom(valueColumn)
                .aggregate(relation.agg_func, filter)
                .buildAsProjection(Expressions.stringifyScalar(value));
        });
        return Relations.aggregate(relation.input, relation.id_columns, measures);
    }
    else if (relation.type == "UNPIVOT") {
        const valueColumns = relation.value_columns ??
            ExpressionUtils.getSchemaColumns(getOutputContext(relation.input)).filter((x) => relation.id_columns.indexOf(x) == -1);
        const unions = [];
        for (const column of valueColumns) {
            const projections = relation.id_columns.concat([
                Expressions.aliasedExpression(column, // THIS IS INTENTIONAL
                relation.variable_alias),
                Expressions.aliasedExpression(Expressions.projectionToExpression(column), relation.value_alias)
            ]);
            unions.push(Relations.project(relation.input, projections));
        }
        if (unions.length > 1) {
            const b = new RelationBuilder(unions[0]);
            return b.union(unions.slice(1), true).build();
        }
        return Relations.union(unions);
    }
    else if (relation.type == "UNPIVOT_PIVOT") {
        const context = getOutputContext(relation.input);
        let valueAlias = "TEMP";
        let i = 1;
        while (context[valueAlias] !== undefined) {
            valueAlias = `TEMP_${i}`;
            i = i + 1;
        }
        const unpivotIds = relation.id_columns.slice();
        if (relation.variable_alias != relation.wide_column) {
            unpivotIds.push(relation.wide_column);
        }
        const unpivot = Relations.unpivot(relation.input, unpivotIds, relation.variable_alias, valueAlias, relation.value_columns);
        const pivotIds = relation.id_columns.filter((x) => x != relation.wide_column);
        if (relation.variable_alias != relation.wide_column) {
            pivotIds.push(relation.variable_alias);
        }
        return Relations.pivot(unpivot, pivotIds, relation.wide_column, relation.wide_values, valueAlias);
    }
    throw Error(`relation not compilable: ${JSON.stringify(relation)}`);
}
function colTypeToVariableType(field) {
    const nonMissing = (field.data ?? []).map((d) => d.rawValue ?? d.value).filter((v) => v != "{Missing}");
    switch (field.type) {
        case "categorical":
            return nonMissing.every((x) => typeof x === "boolean")
                ? "BOOLEAN"
                : nonMissing.every((x) => typeof x === "number")
                    ? "NUMERIC"
                    : "TEXT";
        case "number":
            return "NUMERIC";
        case "date":
            return "DATE";
    }
}
export function isValid(relation) {
    return relation !== undefined && validate(relation).length === 0;
}
export function isEqual(a, b, shallow = false) {
    if (a === undefined) {
        return b === undefined;
    }
    else if (b === undefined) {
        return false;
    }
    switch (a.type) {
        case "AGGREGATE":
            return (b.type === "AGGREGATE" &&
                (shallow || isEqual(a.input, b.input)) &&
                ExpressionUtils.allProjectionsEqual(a.groupings, b.groupings) &&
                ExpressionUtils.allProjectionsEqual(a.measures, b.measures));
        case "DATA_SOURCE":
            return (b.type === "DATA_SOURCE" && a.source.url === b.source.url && a.source.content.type === b.source.content.type);
        case "DISTINCT":
            return (b.type === "DISTINCT" &&
                (shallow || isEqual(a.input, b.input)) &&
                ((a.subset !== undefined &&
                    b.subset !== undefined &&
                    ExpressionUtils.allProjectionsEqual(a.subset, b.subset)) ||
                    (a.subset === undefined && b.subset === undefined)));
        case "EXCLUDE":
            return (b.type === "EXCLUDE" &&
                (shallow || isEqual(a.input, b.input)) &&
                ExpressionUtils.allProjectionsEqual(a.exclude_columns, b.exclude_columns));
        case "FILTER":
            return (b.type === "FILTER" && (shallow || isEqual(a.input, b.input)) && ConditionUtils.isEqual(a.filter, b.filter));
        case "JOIN":
            return (b.type === "JOIN" &&
                (shallow || (isEqual(a.left, b.left) && isEqual(a.right, b.right))) &&
                a.how === b.how &&
                a.suffix === b.suffix &&
                isArrEqual(a.conditions, b.conditions, (x, y) => getLeftKey(x) === getLeftKey(y) && getRightKey(x) === getRightKey(y)));
        case "MUTATE":
            return (b.type === "MUTATE" &&
                (shallow || isEqual(a.input, b.input)) &&
                ExpressionUtils.allProjectionsEqual(a.mutate, b.mutate));
        case "PROJECT":
            return (b.type === "PROJECT" &&
                (shallow || isEqual(a.input, b.input)) &&
                ExpressionUtils.allProjectionsEqual(a.project, b.project));
        case "PIVOT":
            return (b.type === "PIVOT" &&
                (shallow || isEqual(a.input, b.input)) &&
                ExpressionUtils.allProjectionsEqual(a.id_columns, b.id_columns) &&
                ExpressionUtils.isProjectionEqual(a.wide_column, b.wide_column) &&
                isArrEqual(a.wide_values, b.wide_values, ExpressionUtils.isEqual) &&
                ExpressionUtils.isProjectionEqual(a.value_column, b.value_column) &&
                a.agg_func === b.agg_func);
        case "UNPIVOT":
            return (b.type === "UNPIVOT" &&
                (shallow || isEqual(a.input, b.input)) &&
                ExpressionUtils.allProjectionsEqual(a.id_columns, b.id_columns) &&
                ExpressionUtils.allProjectionsEqual(a.value_columns, b.value_columns) &&
                a.variable_alias === b.variable_alias &&
                a.value_alias === b.value_alias);
        case "UNPIVOT_PIVOT":
            return (b.type === "UNPIVOT_PIVOT" &&
                (shallow || isEqual(a.input, b.input)) &&
                ExpressionUtils.allProjectionsEqual(a.id_columns, b.id_columns) &&
                ExpressionUtils.allProjectionsEqual(a.value_columns, b.value_columns) &&
                ExpressionUtils.isProjectionEqual(a.wide_column, b.wide_column) &&
                isArrEqual(a.wide_values, b.wide_values, ExpressionUtils.isEqual) &&
                a.variable_alias === b.variable_alias);
        case "SORT":
            return (b.type === "SORT" &&
                (shallow || isEqual(a.input, b.input)) &&
                isArrEqual(a.sort_by, b.sort_by, (x, y) => x.column === y.column && x.direction === y.direction));
        case "SQL":
            return (b.type == "SQL" &&
                (shallow || isEqual(a.input, b.input)) &&
                a.query == b.query &&
                a.dialect == b.dialect &&
                a.virtual_table_name == b.virtual_table_name &&
                isArrEqual(a.aliased_datasets, b.aliased_datasets, (x, y) => x.alias == y.alias && x.dataset_id == y.dataset_id));
        case "UNION":
            return b.type === "UNION" && isArrEqual(a.inputs, b.inputs, isEqual);
    }
    throw Error(`unknown relation: ${JSON.stringify(a)}`);
}
export function simplify(relation) {
    const tag = relation.tag;
    if (tag !== undefined) {
        relation = { ...relation };
        relation.tag = {
            dataset_id: tag.dataset_id
        };
    }
    if ("input" in relation) {
        if (relation.type === "EXCLUDE" &&
            relation.input.type === "EXCLUDE" &&
            isArrEqual(relation.exclude_columns.slice().sort(), relation.input.exclude_columns.slice().sort())) {
            return simplify(relation.input);
        }
        if ((relation.type === "MUTATE" && relation.mutate.length === 0) ||
            (relation.type === "EXCLUDE" && relation.exclude_columns.length === 0)) {
            return simplify(relation.input);
        }
        if (relation.input.tag?.dataset_id === undefined) {
            if (relation.type === "EXCLUDE" && relation.input.type === "EXCLUDE") {
                const currentExcludes = new Set(relation.exclude_columns);
                const newExcludes = relation.input.exclude_columns.filter((c) => !currentExcludes.has(c));
                return simplify({
                    ...relation,
                    input: relation.input.input,
                    exclude_columns: relation.exclude_columns.concat(newExcludes)
                });
            }
            if (relation.type === "MUTATE" && relation.input.type === "MUTATE") {
                const currentReferences = relation.mutate
                    .map(Expressions.projectionToExpression)
                    .flatMap(ExpressionUtils.getColumnReferences)
                    .map((c) => c.column);
                const inputMutates = new Set(relation.input.mutate.map((expr) => ExpressionUtils.getAlias(expr)));
                if (!currentReferences.some((col) => inputMutates.has(col))) {
                    return simplify({
                        ...relation,
                        input: relation.input.input,
                        mutate: relation.mutate.concat(relation.input.mutate)
                    });
                }
            }
        }
        if (relation.type === "EXCLUDE") {
            return simplify(compile(relation));
        }
        return {
            ...relation,
            input: simplify(relation.input)
        };
    }
    else if (relation.type === "JOIN") {
        return {
            ...relation,
            left: simplify(relation.left),
            right: simplify(relation.right)
        };
    }
    else if (relation.type === "UNION") {
        return Relations.union(relation.inputs.map(simplify));
    }
    else {
        return relation;
    }
}
export function toList(relation) {
    if ("input" in relation) {
        if (relation.type == "PROJECT") {
            if (relation.project.every((x) => typeof x == "string") &&
                isArrEqual(relation.project, ExpressionUtils.getSchemaColumns(getOutputContext(relation.input)))) {
                return toList(relation.input);
            }
        }
        return toList(relation.input).concat([relation]);
    }
    switch (relation.type) {
        case "DATA_SOURCE":
            return [relation];
        case "JOIN":
            return toList(relation.left).concat([relation]);
        case "UNION":
            return relation.inputs.length < 1 ? [relation] : toList(relation.inputs[0]).concat([relation]);
    }
}
export function toComposableList(relation) {
    return toList(relation).filter((r) => r.type != "DATA_SOURCE");
}
export function getSchemaItemsFromDataset(dataset) {
    if (dataset === undefined) {
        return [];
    }
    const sourceSchemaItems = dataset.source.content.schema ?? [];
    const sourceSchemaItemsByName = sourceSchemaItems.reduce((obj, x) => {
        obj[x.name] = x;
        return obj;
    }, {});
    const fieldSchemaItems = (dataset.labels ?? [])
        .map((l) => dataset.fields[l])
        .map((field) => ({
        name: field.label,
        type: colTypeToVariableType(field),
        values: field.type !== "categorical"
            ? undefined
            : (field?.data ?? []).map((x) => x.rawValue ?? x.value).filter((x) => x != "{Missing}" && x != "{Other}")
    }));
    const schemaItems = [];
    for (const item of fieldSchemaItems) {
        const sourceSchemaItem = sourceSchemaItemsByName[item.name];
        if (
        // prefer schema items from fields because they have values
        (item.values === undefined || item.values.length == 0) &&
            sourceSchemaItem !== undefined &&
            // only prefer source schema items if they disagree with a more specific type
            sourceSchemaItem.type !== item.type &&
            sourceSchemaItem.type !== "TEXT") {
            schemaItems.push(sourceSchemaItem);
        }
        else {
            schemaItems.push(item);
        }
    }
    return schemaItems;
}
export class RelationBuilder extends ExpressionUtils.Builder {
    withTag(datasetId, schema) {
        return new RelationBuilder({
            ...this.state,
            tag: {
                dataset_id: datasetId,
                schema
            }
        });
    }
    cherryPick(relation) {
        const update = { ...relation };
        delete update.tag;
        if ("input" in update) {
            if (update.type === "PROJECT") {
                const rContext = getOutputContext(update);
                const excluded = new Set(ExpressionUtils.getSchemaColumns(getOutputContext(update.input)).filter((c) => c in rContext));
                const rNonTrivialExprs = update.project.filter((expr) => typeof expr != "string");
                const rExprsByAlias = rNonTrivialExprs.reduce((map, expr) => {
                    map[ExpressionUtils.getAlias(expr)] = expr;
                    return map;
                }, {});
                const context = this.currentContext();
                const columns = ExpressionUtils.getSchemaColumns(context);
                const exprs = columns
                    .filter((c) => excluded.has(c))
                    .map((c) => rExprsByAlias[c] ?? c);
                const newExprs = rNonTrivialExprs.filter((expr) => context[ExpressionUtils.getAlias(expr)] === undefined);
                return this.project(exprs.concat(newExprs));
            }
            update.input = this.state;
        }
        else if (update.type == "JOIN") {
            update.left = this.state;
        }
        else if (update.type == "UNION") {
            update.inputs = [this.state].concat(update.inputs.slice(1));
        }
        return new RelationBuilder(update);
    }
    currentContext() {
        return getOutputContext(this.state);
    }
    filter(filter) {
        return new RelationBuilder(Relations.filter(this.state, ExpressionUtils.buildOrReturn(filter)));
    }
    project(project) {
        return new RelationBuilder(Relations.project(this.state, project.slice()));
    }
    mutate(mutate) {
        return new RelationBuilder(Relations.mutate(this.state, mutate.slice()));
    }
    exclude(excludeColumns) {
        return new RelationBuilder(Relations.exclude(this.state, excludeColumns.slice()));
    }
    aggregate(groupings, measures) {
        return new RelationBuilder(Relations.aggregate(this.state, groupings.slice(), measures.slice()));
    }
    distinct(subset) {
        return new RelationBuilder(Relations.distinct(this.state, subset?.slice()));
    }
    pivot(idColumns, wideColumn, wideValues, valueColumn, aggFunc = "first") {
        return new RelationBuilder(Relations.pivot(this.state, idColumns.slice(), wideColumn, wideValues.slice(), valueColumn, aggFunc));
    }
    unpivot(idColumns, variableAlias, valueAlias, valueColumns) {
        return new RelationBuilder(Relations.unpivot(this.state, idColumns.slice(), variableAlias, valueAlias, valueColumns?.slice()));
    }
    unpivotAndPivot(idColumns, valueColumns, wideColumn, wideValues, variableAlias) {
        return new RelationBuilder(Relations.unpivotAndPivot(this.state, idColumns.slice(), valueColumns.slice(), wideColumn, wideValues.slice(), variableAlias));
    }
    join(right, conditions, how = "LEFT", suffix = "_right") {
        return new RelationBuilder(Relations.join(this.state, ExpressionUtils.buildOrReturn(right), conditions.slice(), how, suffix));
    }
    sort(sort_by) {
        return new RelationBuilder(Relations.sort(this.state, sort_by.slice()));
    }
    union(buildableInputs, autoCast = false) {
        const currentContext = getOutputContext(this.state);
        let inputs = buildableInputs.map((input) => ExpressionUtils.buildOrReturn(input));
        if (autoCast) {
            inputs = inputs.map((input) => {
                const context = getOutputContext(input);
                const mutates = ExpressionUtils.getSchemaColumns(context)
                    .filter((name) => currentContext[name]?.type != context[name]?.type)
                    .map(([name, _]) => {
                    const currentType = currentContext[name]?.type;
                    if (currentType == "NUMERIC" || currentType == "TEXT") {
                        return ExpressionUtils.col(name).cast(currentType).buildAsProjection(name);
                    }
                    else {
                        return undefined;
                    }
                })
                    .filter(filterUndefined);
                return Relations.mutate(input, mutates);
            });
        }
        inputs.unshift(this.state);
        return new RelationBuilder(Relations.union(inputs));
    }
    sql(query, dialect, virtual_table_name, aliased_datasets) {
        return new RelationBuilder(Relations.sql(this.state, query, dialect, virtual_table_name, aliased_datasets));
    }
    static fromDataset(dataset) {
        const input = dataset?.transformations?.plan === undefined
            ? Relations.readDataSource(dataset?.source ?? {
                url: "",
                content: {
                    type: "RAW_DATA"
                }
            })
            : dataset.transformations.plan.relation;
        return new RelationBuilder(input).withTag(dataset?.id ?? -1, getSchemaItemsFromDataset(dataset));
    }
}
export function builder(relation) {
    return new RelationBuilder(relation);
}
export function fromDataset(dataset) {
    return RelationBuilder.fromDataset(dataset).build();
}
export function builderFromDataset(dataset) {
    return RelationBuilder.fromDataset(dataset);
}
export function fromDataSource(source) {
    return builder(Relations.readDataSource(source));
}
export class DefaultRelationFactory {
    builder;
    constructor(builder) {
        this.builder = builder;
    }
    filter() {
        return this.builder.filter(ExpressionUtils.defaultExpressionFactory().condition()).build();
    }
    project() {
        return this.builder.project(ExpressionUtils.getSchemaColumns(this.builder.currentContext())).build();
    }
    mutate() {
        return this.builder.mutate([]).build();
    }
    exclude() {
        return this.builder.exclude([]).build();
    }
    aggregate() {
        return this.builder.aggregate([], []).build();
    }
    distinct() {
        return this.builder.distinct([]).build();
    }
    pivot() {
        return this.builder.pivot([], "", [], "").build();
    }
    unpivot() {
        return this.builder.unpivot(ExpressionUtils.getSchemaColumns(this.builder.currentContext()), "", "").build();
    }
    unpivotAndPivot() {
        return this.builder.unpivotAndPivot([], [], "", [], "").build();
    }
    join() {
        return this.builder.join(this.builder.build(), [{ key: "" }], "LEFT").build();
    }
    union() {
        return this.builder.union([]).build();
    }
    sql() {
        const sources = getSources(this.builder.build());
        const dialect = sources.length == 0 ? "duckdb" : getSourceDialect(sources[0]);
        return this.builder.sql("", dialect, "").build();
    }
    static from(relation) {
        return new DefaultRelationFactory(builder(relation));
    }
}
export function convertPropertyToExpression(property) {
    const expression = col(property.column);
    if (isInterval(property)) {
        return expression.isBetween(property.start, property.end);
    }
    else if (property.value == undefined) {
        return expression.isNull();
    }
    else {
        return expression.eq(property.value);
    }
}
export function buildFilterExpression(properties) {
    const constrains = properties.filter((property) => isNotColumn(property));
    return andAll(constrains.map((constrainProperty) => convertPropertyToExpression(constrainProperty)).filter(filterUndefined));
}
export function defaultFactory(dataset) {
    return new DefaultRelationFactory(builderFromDataset(dataset));
}
export function ensureValidTarget(relation, target) {
    return isValidTarget(relation, target) ? target : undefined;
}
export function ensureValidTargetValue(relation, target, targetValue) {
    if (target === undefined) {
        return undefined;
    }
    const output = getOutputContext(relation)[target];
    if (typeof targetValue === "string" && output?.type == "NUMERIC") {
        try {
            targetValue = parseFloat(targetValue);
        }
        catch {
            // pass
        }
    }
    return output?.values?.find((v) => v === targetValue);
}
export function outputIsValidForTarget(output) {
    if (output === undefined) {
        return false;
    }
    switch (output.type) {
        case "BOOLEAN":
        case "NUMERIC":
            return true;
        case "NULL":
        case "DATE":
            return false;
        case "TEXT":
            return (output.values?.length ?? 2) > 1;
    }
}
export function getOutcomeOptionsFromOutputContext(context) {
    const targetContext = ExpressionUtils.getFilteredContext(context, outputIsValidForTarget);
    return ExpressionUtils.getNamedOutputs(targetContext).map((output) => {
        return {
            label: output.name,
            value: output.name,
            type: ((type) => {
                switch (type) {
                    case "NUMERIC":
                        return "number";
                    case "DATE":
                        return "date";
                    case "TEXT":
                    case "BOOLEAN":
                    case "NULL":
                    default:
                        return "categorical";
                }
            })(output.type)
        };
    });
}
export function getOutcomeSuccessOptionsFromOutputContext(context, target) {
    const output = context[target];
    if (output === undefined || output.values === undefined) {
        return [];
    }
    return output.values
        .filter((v) => v !== "{Missing}")
        .map((v) => ({
        value: v,
        label: String(v)
    }));
}
export function isValidTarget(relation, target) {
    if (target === undefined) {
        return false;
    }
    const output = getOutputContext(relation)[target];
    return outputIsValidForTarget(output);
}
